import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: string;
}
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  // Handle keyboard interactions and focus management
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Tab') {
        // Focus trap
        const focusableElements = dialogRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as NodeListOf<HTMLElement>;
        if (!focusableElements?.length) return;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    // Focus the confirm button when dialog opens
    if (confirmButtonRef.current) {
      setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 100);
    }
    // Lock body scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalStyle;
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-59">
      {isOpen && <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="dialog-title" data-id="element-60">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-61" />
          {/* Dialog positioning */}
          <div className="fixed inset-0 z-10 overflow-y-auto" data-id="element-62">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0" data-id="element-63">
              {/* Dialog content */}
              <motion.div ref={dialogRef} initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} className="relative w-full max-w-[90vw] sm:max-w-md transform overflow-hidden rounded-xl bg-gradient-to-b from-[#0a1642] to-[#050b2e] p-4 sm:p-6 text-left shadow-2xl border border-white/10 sm:my-8" onClick={e => e.stopPropagation()} data-id="element-64">
                {/* Close button */}
                <div className="absolute right-4 top-4" data-id="element-65">
                  <button type="button" className="rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-[#0a1642]" onClick={onClose} aria-label="Close dialog" data-id="element-66">
                    <XIcon className="h-5 w-5" data-id="element-67" />
                  </button>
                </div>
                {/* Title */}
                <div className="mt-1 mb-3 sm:mb-4" data-id="element-68">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white pr-8" id="dialog-title" data-id="element-69">
                    {title}
                  </h3>
                </div>
                {/* Message */}
                {message && <div className="mt-2 mb-4 sm:mb-6" data-id="element-70">
                    <p className="text-white/80 text-sm sm:text-base" data-id="element-71">
                      {message}
                    </p>
                  </div>}
                {/* Actions */}
                <div className="mt-4 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3" data-id="element-72">
                  <button type="button" className="inline-flex justify-center rounded-lg border border-white/10 bg-transparent px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-[#0a1642] transition-colors" onClick={onClose} data-id="element-73">
                    No
                  </button>
                  <button type="button" ref={confirmButtonRef} className="inline-flex justify-center rounded-lg bg-gradient-to-r from-blue-400 to-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:from-blue-500 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-[#0a1642] transition-all" onClick={onConfirm} data-id="element-74">
                    Yes
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>}
    </AnimatePresence>;
};